/**
 * room-geometry.js — Home Cinema Supply
 *
 * Pure calculation engine for the "Will this fit?" checker and the room planner.
 * NO DOM ACCESS. Every function here takes plain numbers/objects and returns
 * plain objects, so it can be unit-tested under node and reused by both phases.
 *
 * ---------------------------------------------------------------------------
 * TUNING
 * ---------------------------------------------------------------------------
 * Every number the geometry depends on lives in DEFAULTS below. These are
 * INDUSTRY RULES OF THUMB, not manufacturer specs. Adjust them there — never
 * inline in the functions. Callers can also override per-call by passing a
 * `constants` object, which is shallow-merged over DEFAULTS.
 *
 * All internal math is in INCHES.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.RoomGeometry = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* =======================================================================
   * TUNABLE CONSTANTS — edit these, and only these, to re-calibrate the tool.
   * ======================================================================= */
  var DEFAULTS = {
    // Viewing distance as a multiple of screen diagonal (seat back to screen).
    VIEWING_DISTANCE_MIN_MULT: 1.2,
    VIEWING_DISTANCE_MAX_MULT: 1.5,

    // Row-to-row spacing, measured seat back to seat back.
    ROW_SPACING_MIN_IN: 60,
    ROW_SPACING_IDEAL_IN: 72,

    // Riser (platform) sizing for the back row.
    RISER_HEIGHT_MIN_IN: 8,
    RISER_HEIGHT_DEFAULT_IN: 12,

    // Open floor required to the left and right of the row, per side.
    SIDE_CLEARANCE_IN: 12,

    // Bottom edge of the screen, measured off the finished floor.
    SCREEN_BOTTOM_HEIGHT_IN: 26,

    // Extra width beyond the bare minimum before we call a room a comfortable
    // "fits" rather than a "tight".
    WIDTH_COMFORT_MARGIN_IN: 12,

    // Minimum headroom above a riser before we warn about ceiling height.
    MIN_HEADROOM_IN: 84,

    // Used when the shopper leaves the screen-size field empty.
    DEFAULT_SCREEN_DIAGONAL_IN: 120,

    // Sanity bounds for shopper input, in inches.
    MIN_ROOM_DIMENSION_IN: 48, // 4 ft
    MAX_ROOM_DIMENSION_IN: 1200, // 100 ft
    MIN_SCREEN_DIAGONAL_IN: 40,
    MAX_SCREEN_DIAGONAL_IN: 300
  };

  var INCHES_PER_FOOT = 12;

  function constants(overrides) {
    if (!overrides) return DEFAULTS;
    var merged = {};
    for (var k in DEFAULTS) if (Object.prototype.hasOwnProperty.call(DEFAULTS, k)) merged[k] = DEFAULTS[k];
    for (var o in overrides) {
      if (Object.prototype.hasOwnProperty.call(overrides, o) && isNum(overrides[o])) merged[o] = overrides[o];
    }
    return merged;
  }

  function isNum(v) {
    return typeof v === 'number' && isFinite(v);
  }

  /* =======================================================================
   * UNITS — parsing shopper input and formatting output
   * ======================================================================= */

  /**
   * Parse a length written the way people actually type it.
   *
   * Accepts: "14", "14.5", "14 ft", "14ft 6in", "14' 6\"", "14-6", "14 6",
   *          "168 in", "6\"", and unicode primes (14′ 6″).
   *
   * @param {string|number} raw
   * @param {string} defaultUnit  'ft' or 'in' — how to read a bare number.
   * @returns {number|null} inches, or null if unparseable / not positive.
   */
  function parseLength(raw, defaultUnit) {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'number') return isFinite(raw) && raw > 0 ? raw * (defaultUnit === 'in' ? 1 : INCHES_PER_FOOT) : null;

    var s = String(raw).trim().toLowerCase();
    if (!s) return null;

    // Normalise smart quotes and primes to plain ' and ".
    s = s.replace(/[′’ʹ]/g, "'").replace(/[″”ʺ]/g, '"');
    // A leading minus is a negative length, not the "14-6" feet/inches form.
    if (/^[-−–—]/.test(s)) return null;
    if (!/\d/.test(s)) return null;
    // Reject stray letters that aren't a unit we understand.
    if (/[a-z]/.test(s.replace(/ft|feet|foot|inches|inch|in/g, ''))) return null;

    var inches = 0;
    var matched = false;
    var rest = s;

    var feetMatch = rest.match(/(\d+(?:\.\d+)?)\s*(?:ft|feet|foot|')/);
    if (feetMatch) {
      inches += parseFloat(feetMatch[1]) * INCHES_PER_FOOT;
      matched = true;
      rest = rest.replace(feetMatch[0], ' ');
    }

    var inchMatch = rest.match(/(\d+(?:\.\d+)?)\s*(?:inches|inch|in|")/);
    if (inchMatch) {
      inches += parseFloat(inchMatch[1]);
      matched = true;
      rest = rest.replace(inchMatch[0], ' ');
    }

    if (matched) {
      // Any leftover bare number after an explicit unit is ambiguous — reject.
      if (/\d/.test(rest)) return null;
      return inches > 0 ? inches : null;
    }

    // No units given. One bare number → defaultUnit. Two → feet then inches.
    var bare = s.match(/\d+(?:\.\d+)?/g) || [];
    if (bare.length === 1) {
      var n = parseFloat(bare[0]);
      if (!(n > 0)) return null;
      return defaultUnit === 'in' ? n : n * INCHES_PER_FOOT;
    }
    if (bare.length === 2 && /^[\d.]+\s*[-\s]\s*[\d.]+$/.test(s)) {
      var ft = parseFloat(bare[0]);
      var i = parseFloat(bare[1]);
      if (!(ft >= 0) || !(i >= 0) || i >= INCHES_PER_FOOT) return null;
      var t = ft * INCHES_PER_FOOT + i;
      return t > 0 ? t : null;
    }
    return null;
  }

  /**
   * Format inches as prose: "14 ft 6 in", "14 ft", "8 in".
   */
  function formatInches(value, opts) {
    if (!isNum(value)) return '—';
    var compact = opts && opts.compact;
    var neg = value < 0;
    var total = Math.round(Math.abs(value));
    var ft = Math.floor(total / INCHES_PER_FOOT);
    var inch = total % INCHES_PER_FOOT;
    var out;
    if (ft === 0) out = compact ? inch + '″' : inch + ' in';
    else if (inch === 0) out = compact ? ft + '′' : ft + ' ft';
    else out = compact ? ft + '′ ' + inch + '″' : ft + ' ft ' + inch + ' in';
    return (neg ? '−' : '') + out;
  }

  /* =======================================================================
   * PRODUCT DATA
   * ======================================================================= */

  // Metafields the checker cannot work without.
  var REQUIRED_KEYS = ['rowWidthIn', 'depthUprightIn', 'depthReclinedIn', 'seatCount', 'wallClearanceIn'];

  /**
   * True when a product carries enough data to render the checker.
   * Note wallClearanceIn === 0 is valid (wall-hugger), so we test for
   * null/undefined rather than falsiness.
   */
  function hasRequiredData(product) {
    if (!product) return false;
    for (var i = 0; i < REQUIRED_KEYS.length; i++) {
      var v = product[REQUIRED_KEYS[i]];
      if (!isNum(v)) return false;
      if (REQUIRED_KEYS[i] !== 'wallClearanceIn' && v <= 0) return false;
      if (v < 0) return false;
    }
    return true;
  }

  /* =======================================================================
   * 1. WIDTH — does the row physically fit the wall?
   * ======================================================================= */

  /**
   * required = row_width + (2 × SIDE_CLEARANCE_IN)
   * fits   if room_width >= required + WIDTH_COMFORT_MARGIN_IN
   * tight  if room_width >= required
   * fails  otherwise
   *
   * A manufacturer `min_room_width`, when supplied, acts as a hard floor and
   * raises `required` if it is larger than our computed figure.
   */
  function checkWidth(product, room, c) {
    c = constants(c);
    var rowW = product.rowWidthIn;
    var roomW = room.widthIn;

    var computedRequired = rowW + 2 * c.SIDE_CLEARANCE_IN;
    var manufacturerMin = isNum(product.minRoomWidthIn) && product.minRoomWidthIn > 0 ? product.minRoomWidthIn : null;
    var required = manufacturerMin !== null ? Math.max(computedRequired, manufacturerMin) : computedRequired;

    var status;
    if (roomW >= required + c.WIDTH_COMFORT_MARGIN_IN) status = 'fits';
    else if (roomW >= required) status = 'tight';
    else status = 'fails';

    var sparePerSide = (roomW - rowW) / 2;

    return {
      status: status,
      rowWidthIn: rowW,
      roomWidthIn: roomW,
      requiredIn: required,
      manufacturerMinIn: manufacturerMin,
      manufacturerMinGoverns: manufacturerMin !== null && manufacturerMin > computedRequired,
      shortfallIn: status === 'fails' ? required - roomW : 0,
      sparePerSideIn: sparePerSide,
      spareTotalIn: roomW - rowW
    };
  }

  /* =======================================================================
   * 2. VIEWING DISTANCE — is the room deep enough to sit at a good distance?
   * ======================================================================= */

  /**
   * ideal_min = screen_diagonal × VIEWING_DISTANCE_MIN_MULT
   * ideal_max = screen_diagonal × VIEWING_DISTANCE_MAX_MULT
   *
   * Measured from the screen wall to the FRONT ROW SEAT BACK — that is where a
   * seated viewer's head actually sits. The furthest back the seat can go is
   * the room length less the wall clearance the recline mechanism needs.
   */
  function checkViewingDistance(product, room, c) {
    c = constants(c);
    var screen = isNum(room.screenDiagonalIn) && room.screenDiagonalIn > 0
      ? room.screenDiagonalIn
      : c.DEFAULT_SCREEN_DIAGONAL_IN;

    var idealMin = screen * c.VIEWING_DISTANCE_MIN_MULT;
    var idealMax = screen * c.VIEWING_DISTANCE_MAX_MULT;

    // Furthest the seat back can sit from the screen in this room.
    var maxSeatBack = room.lengthIn - product.wallClearanceIn;

    var status, recommendedIn;
    if (maxSeatBack < idealMin) {
      status = 'shallow'; // cannot get far enough back; screen will feel large
      recommendedIn = maxSeatBack;
    } else if (maxSeatBack > idealMax) {
      status = 'ideal'; // room to spare — pull the row forward off the wall
      recommendedIn = idealMax;
    } else {
      status = 'ideal';
      recommendedIn = maxSeatBack;
    }

    return {
      status: status,
      screenDiagonalIn: screen,
      screenWasDefaulted: !(isNum(room.screenDiagonalIn) && room.screenDiagonalIn > 0),
      idealMinIn: idealMin,
      idealMaxIn: idealMax,
      maxSeatBackIn: maxSeatBack,
      recommendedIn: recommendedIn,
      // How far off the ideal band they are, when shallow.
      shortfallIn: status === 'shallow' ? idealMin - maxSeatBack : 0,
      // True when the row can be positioned anywhere inside the ideal band.
      bandReachable: maxSeatBack >= idealMin
    };
  }

  /* =======================================================================
   * 3. SECOND ROW — is there depth for another row behind the first?
   * ======================================================================= */

  /**
   * needs = depth_reclined + ROW_SPACING_MIN_IN + depth_reclined + wall_clearance
   *
   * Read front-to-back: the front row's reclined footprint, the gap to the row
   * behind it, the back row's reclined footprint, and the clearance the back
   * row needs behind it to recline against the wall. Compared against the full
   * screen-wall-to-back-wall depth.
   */
  function checkSecondRow(product, room, c) {
    c = constants(c);
    var d = product.depthReclinedIn;
    var needsMin = d + c.ROW_SPACING_MIN_IN + d + product.wallClearanceIn;
    var needsIdeal = d + c.ROW_SPACING_IDEAL_IN + d + product.wallClearanceIn;

    var available = room.lengthIn;
    var possible = available >= needsMin;
    var comfortable = available >= needsIdeal;

    return {
      possible: possible,
      comfortable: comfortable,
      neededIn: needsMin,
      neededIdealIn: needsIdeal,
      availableIn: available,
      shortfallIn: possible ? 0 : needsMin - available,
      spareIn: possible ? available - needsMin : 0,
      // Actual back-to-back spacing achievable with the depth on hand.
      achievableSpacingIn: Math.min(c.ROW_SPACING_IDEAL_IN, available - (2 * d + product.wallClearanceIn))
    };
  }

  /* =======================================================================
   * 4. RISER — recommended whenever a second row is placed
   * ======================================================================= */

  function checkRiser(secondRow, room, c) {
    c = constants(c);
    if (!secondRow.possible) {
      return { recommended: false, heightIn: 0, headroomIn: null, headroomOk: true, ceilingKnown: false };
    }
    var height = c.RISER_HEIGHT_DEFAULT_IN;
    var ceilingKnown = isNum(room.ceilingHeightIn) && room.ceilingHeightIn > 0;
    var headroom = ceilingKnown ? room.ceilingHeightIn - height : null;
    var headroomOk = ceilingKnown ? headroom >= c.MIN_HEADROOM_IN : true;

    // If the ceiling is low, fall back toward the minimum usable riser.
    var reducedHeight = null;
    if (ceilingKnown && !headroomOk) {
      var maxFit = room.ceilingHeightIn - c.MIN_HEADROOM_IN;
      reducedHeight = maxFit >= c.RISER_HEIGHT_MIN_IN ? Math.floor(maxFit) : null;
    }

    return {
      recommended: true,
      heightIn: height,
      minHeightIn: c.RISER_HEIGHT_MIN_IN,
      ceilingKnown: ceilingKnown,
      headroomIn: headroom,
      headroomOk: headroomOk,
      minHeadroomIn: c.MIN_HEADROOM_IN,
      reducedHeightIn: reducedHeight
    };
  }

  /**
   * Sightline helper (used by the planner): the back row's eye line must clear
   * the head in front. Returns the rise needed at the back row's distance.
   */
  function sightlineRise(frontRowDistanceIn, backRowDistanceIn, c) {
    c = constants(c);
    var run = backRowDistanceIn - frontRowDistanceIn;
    if (run <= 0) return 0;
    // Simple similar-triangles approximation against the screen bottom edge.
    var rise = (run / Math.max(frontRowDistanceIn, 1)) * c.SCREEN_BOTTOM_HEIGHT_IN;
    return Math.max(0, rise);
  }

  /* =======================================================================
   * 5. EVALUATE — the whole verdict in one call
   * ======================================================================= */

  function evaluate(product, room, overrides) {
    var c = constants(overrides);
    if (!hasRequiredData(product)) return null;

    var width = checkWidth(product, room, c);
    var viewing = checkViewingDistance(product, room, c);
    var secondRow = checkSecondRow(product, room, c);
    var riser = checkRiser(secondRow, room, c);

    // Overall verdict is driven by width — that is the pass/fail question the
    // shopper actually asked. Depth problems downgrade a pass to a warning.
    var overall = width.status;
    if (overall === 'fits' && viewing.status === 'shallow') overall = 'tight';

    return {
      overall: overall,
      width: width,
      viewing: viewing,
      secondRow: secondRow,
      riser: riser,
      constants: c
    };
  }

  /* =======================================================================
   * Validation of raw shopper input, shared by both phases.
   * ======================================================================= */

  function validateRoom(fields, overrides) {
    var c = constants(overrides);
    var errors = {};
    var out = {};

    var w = parseLength(fields.width, 'ft');
    if (w === null) errors.width = 'Enter a width, for example 14 or 14 ft 6 in.';
    else if (w < c.MIN_ROOM_DIMENSION_IN || w > c.MAX_ROOM_DIMENSION_IN) {
      errors.width = 'Enter a width between ' + formatInches(c.MIN_ROOM_DIMENSION_IN) + ' and ' + formatInches(c.MAX_ROOM_DIMENSION_IN) + '.';
    } else out.widthIn = w;

    var l = parseLength(fields.length, 'ft');
    if (l === null) errors.length = 'Enter a length, for example 20 or 20 ft 6 in.';
    else if (l < c.MIN_ROOM_DIMENSION_IN || l > c.MAX_ROOM_DIMENSION_IN) {
      errors.length = 'Enter a length between ' + formatInches(c.MIN_ROOM_DIMENSION_IN) + ' and ' + formatInches(c.MAX_ROOM_DIMENSION_IN) + '.';
    } else out.lengthIn = l;

    // Screen is optional and measured in inches (diagonal).
    if (fields.screen === undefined || fields.screen === null || String(fields.screen).trim() === '') {
      out.screenDiagonalIn = c.DEFAULT_SCREEN_DIAGONAL_IN;
    } else {
      var s = parseLength(fields.screen, 'in');
      if (s === null) errors.screen = 'Enter a screen size in inches, for example 120.';
      else if (s < c.MIN_SCREEN_DIAGONAL_IN || s > c.MAX_SCREEN_DIAGONAL_IN) {
        errors.screen = 'Enter a screen size between ' + c.MIN_SCREEN_DIAGONAL_IN + ' and ' + c.MAX_SCREEN_DIAGONAL_IN + ' inches.';
      } else out.screenDiagonalIn = s;
    }

    // Ceiling is optional.
    if (fields.ceiling !== undefined && fields.ceiling !== null && String(fields.ceiling).trim() !== '') {
      var ch = parseLength(fields.ceiling, 'ft');
      if (ch === null) errors.ceiling = 'Enter a ceiling height, for example 8 or 7 ft 6 in.';
      else out.ceilingHeightIn = ch;
    }

    return { room: out, errors: errors, valid: Object.keys(errors).length === 0 };
  }

  return {
    DEFAULTS: DEFAULTS,
    INCHES_PER_FOOT: INCHES_PER_FOOT,
    parseLength: parseLength,
    formatInches: formatInches,
    hasRequiredData: hasRequiredData,
    checkWidth: checkWidth,
    checkViewingDistance: checkViewingDistance,
    checkSecondRow: checkSecondRow,
    checkRiser: checkRiser,
    sightlineRise: sightlineRise,
    validateRoom: validateRoom,
    evaluate: evaluate
  };
});
